import React, { useCallback, useState } from "react";
import { Trans } from "../../utils/i18n";
import classNames from "classnames";
import { Alert, Button } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { getCookieValue } from "../../utils/getCookieValue";
import { useFormState } from "../FormState";
import Markdown from "react-markdown";
import { flatten } from "lodash";
import { SelfRepairFlowMetaData } from "../../Pages/SelfRepairGuides/SelfRepairFlowMetaData";
import { SelfRepairTool } from "../../Pages/SelfRepairGuides/SelfRepairTool";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/captions.css";
import DownloadIcon from '@mui/icons-material/Download';

const SliderBulletContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 8px;
    margin-bottom: 8px;
`;

export default function SelfRepairInstructions({ values, displayFilesOnly }) {
    const [slides, setSlides] = useState([]);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [index, setIndex] = useState(0);
    const orderEntryState = useFormState();
    const product =
        orderEntryState.selectedProduct?.id ||
        orderEntryState?.serialNumber?.product?.id;
    const problem_2 = values.problem_2;

    const onCloseLightbox = () => {
        setIsLightboxOpen(false);
        setSlides([]);
        setIndex(0);
    };

    const openLightbox = ({ index, slides }) => {
        setSlides(slides);
        setIndex(index);
        setIsLightboxOpen(true);
    };

    const fetchFlow = useCallback(() => {
        return fetch(
            route("selfrepair.getFlow") +
                `?product_id=${product}${
                    problem_2 ? `&problem_id=${problem_2}` : ""
                }`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                    "X-Requested-With": "XMLHttpRequest",
                },
            }
        ).then((response) => response.json());
    }, [product, problem_2]);

    const { data, isLoading } = useQuery({
        queryKey: ["flows", product, problem_2],
        queryFn: fetchFlow,
    });

    const flattenSteps = flatten(
        data?.data?.steps?.map((parentStep) =>
            [
                parentStep.children?.length === 0 ? parentStep : null,
                ...(parentStep.children ?? [])?.map((childStep) => ({
                    ...childStep,
                    name: parentStep.name,
                })),
            ].filter(Boolean)
        ) ?? []
    );

    if (data?.data?.files && data?.data?.files.length > 0 && displayFilesOnly) {
        return (
            <div className="mt-9">
                <h3>
                    <Trans i18nKey="orderEntry.selfFix.downloadGuides">
                        Download Self Repair Guides
                    </Trans>
                </h3>

                <ul className="space-y-4 list-none p-0 m-0">
                    {data?.data?.files.map((file) => {
                        const extension = file.name?.split?.('.').pop?.().toUpperCase?.();

                        return (
                            <li key={file.id} className="bg-white b2x-shadow-md w-full p-6 rounded-lg">
                                <div className="flex flex-col items-start sm:flex-row sm:items-center gap-4">
                                    {extension && (
                                        <div className="shrink-0 w-12 h-12 rounded-md bg-primary flex items-center justify-center text-white select-none cursor-default">{extension}</div>
                                    )}
                                    <div className="flex items-center gap-2 truncate">
                                        <a href={file.path} target="_blank" className="truncate font-semibold text-primary no-underline" rel="noreferrer">
                                            {file.name}
                                        </a>
                                        <a href={file.path} target="_blank" className="inline-flex text-primary no-underline" rel="noreferrer">
                                            <DownloadIcon className="shrink-0" />
                                        </a>
                                    </div>

                                    {file.size && (
                                        <div className="text-gray-500 sm:ml-auto shrink-0">
                                            {(file.size/1024/1024).toFixed(2)} MB
                                        </div>
                                    )}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        )
    }

    return (
        <div
            className={classNames({
                "bg-white b2x-shadow-md w-full mt-9 p-6 md:p-9": true,
            })}
        >
            <div className="space-y-8">
                <div className="space-y-4">
                    <SelfRepairFlowMetaData
                        complexity={data?.data?.complexity}
                        estimatedTime={data?.data?.time}
                        targetUser={data?.data?.target_user}
                    />

                    <h2 className="text-2xl leading-tight my-0">
                        {data?.data?.name}
                    </h2>

                    {data?.data?.description && (
                        <Markdown>{data?.data?.description}</Markdown>
                    )}

                    {data?.data?.selfRepairTools?.length > 0 && (
                        <div>
                            <h2 className="text-lg">
                                <Trans i18nKey="selfRepair.guide.requiredTools.title" />
                            </h2>
                            <p>
                                <Trans i18nKey="selfRepair.guide.requiredTools.description" />
                            </p>

                            <div className="flex flex-wrap gap-4">
                                {data?.data?.selfRepairTools?.map((tool) => (
                                    <SelfRepairTool
                                        image={tool.image}
                                        name={tool.name}
                                        key={tool.id}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {data?.data?.warnings && (
                        <Alert severity="warning">
                            <div className="font-semibold text-base">
                                <Trans i18nKey="selfRepair.guide.warnings" />
                            </div>

                            <Markdown>{data?.data?.warnings}</Markdown>
                        </Alert>
                    )}
                </div>

                <div>
                    <Swiper
                        autoHeight
                        loop={(flattenSteps ?? []).length > 1}
                        modules={[Navigation, Pagination, A11y]}
                        spaceBetween={0}
                        slidesPerView={1}
                        pagination={{
                            clickable: true,
                            el: "#swiper-bullet-container",
                            bulletClass: "swiper-custom-bullet-rect",
                            bulletActiveClass:
                                "swiper-custom-bullet-rect--active",
                        }}
                        navigation={{
                            enabled: true,
                            nextEl: ".swiper-right-button",
                            prevEl: ".swiper-left-button",
                        }}
                    >
                        {flattenSteps?.map((item, index) => (
                            <SwiperSlide
                                key={`${index}-${item.id}`}
                                style={{ height: "auto" }}
                            >
                                <h3>{item.name}</h3>
                                <div
                                    className={classNames(
                                        "gap-4 grid grid-cols-12"
                                    )}
                                >
                                    <div
                                        className={classNames(
                                            "col-span-12 flex flex-col justify-center",
                                            {
                                                "md:col-span-6":
                                                    item?.layout !==
                                                    "image_top_text_bottom",
                                                "md:col-span-12":
                                                    item?.layout ===
                                                    "image_top_text_bottom",
                                                "order-first":
                                                    item?.layout ===
                                                    "image_left_text_right",
                                                "order-last":
                                                    item?.layout ===
                                                    "image_right_text_left",
                                            }
                                        )}
                                    >
                                        <Swiper
                                            className="w-full"
                                            autoHeight
                                            loop={
                                                (item.images ?? []).length > 1
                                            }
                                            modules={[
                                                Navigation,
                                                Pagination,
                                                A11y,
                                            ]}
                                            spaceBetween={0}
                                            nested={true}
                                            slidesPerView={1}
                                            pagination={{
                                                clickable: true,
                                                el: "#swiper-bullet-container-inner",
                                                bulletClass:
                                                    "swiper-custom-bullet-circle",
                                                bulletActiveClass:
                                                    "swiper-custom-bullet-circle--active",
                                            }}
                                        >
                                            {item.images.map((image, index) => (
                                                <SwiperSlide key={image.id}>
                                                    <img
                                                        className={
                                                            "object-cover w-full h-64 cursor-pointer rounded-lg"
                                                        }
                                                        src={image.path}
                                                        onClick={() =>
                                                            openLightbox({
                                                                index,
                                                                slides: item.images.map(
                                                                    (
                                                                        image
                                                                    ) => ({
                                                                        src: image.path,
                                                                        title: item.name,
                                                                        description:
                                                                            (
                                                                                <Markdown>
                                                                                    {
                                                                                        item.description
                                                                                    }
                                                                                </Markdown>
                                                                            ),
                                                                    })
                                                                ),
                                                            })
                                                        }
                                                    />
                                                </SwiperSlide>
                                            ))}

                                            <div
                                                id="swiper-bullet-container-inner"
                                                className="mt-2 flex items-center justify-center gap-2"
                                            />
                                        </Swiper>
                                    </div>

                                    <div
                                        className={classNames("col-span-12", {
                                            "md:col-span-6":
                                                item?.layout !==
                                                "image_top_text_bottom",
                                            "md:col-span-12":
                                                item?.layout ===
                                                "image_top_text_bottom",
                                            "order-first":
                                                item?.layout ===
                                                "text_left_image_right",
                                            "order-last":
                                                item?.layout ===
                                                "text_right_image_left",
                                        })}
                                    >
                                        {item.title && <h3>{item.title}</h3>}
                                        <Markdown>{item.description}</Markdown>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <SliderBulletContainer
                        id="swiper-bullet-container"
                        className="flex items-center justify-center gap-2"
                    />
                </div>
            </div>

            <div className={"col-span-12 flex flex-row justify-between"}>
                <Button
                    onClick={() => {}}
                    color={"secondary"}
                    variant={"outlined"}
                    size={"small"}
                    className={"swiper-left-button"}
                >
                    <Trans i18nKey="common.previous">Previous</Trans>
                </Button>
                <Button
                    onClick={() => {}}
                    color={"secondary"}
                    variant={"outlined"}
                    size={"small"}
                    className={"swiper-right-button"}
                >
                    <Trans i18nKey="common.next">Next</Trans>
                </Button>
            </div>

            {data?.data && data?.data?.productcategory && (
                <div className={"col-span-12 flex flex-row justify-center"}>
                    <a
                        href={route("selfrepair.guides.showCategory", {
                            category: data?.data?.productcategory?.slug
                        })}
                        target="_blank"
                        className={"cursor-pointer underline font-semibold text-sm text-secondary"}
                    >
                        <Trans i18nKey="orderEntry.selfFix.checkAllTheResources">
                            Check all the resources
                        </Trans>
                    </a>
                </div>
            )}

            <Lightbox
                carousel={{
                    finite: true,
                }}
                captions={{
                    descriptionMaxLines: 5,
                    descriptionTextAlign: "center",
                    showToggle: true,
                }}
                controller={{
                    closeOnPullUp: true,
                    closeOnBackdropClick: true,
                    closeOnPullDown: true,
                }}
                plugins={[Zoom, Thumbnails, Fullscreen, Captions]}
                slides={slides}
                open={isLightboxOpen}
                close={onCloseLightbox}
                index={index}
                zoom={{
                    maxZoomPixelRatio: 5,
                    scrollToZoom: true,
                }}
            />
        </div>
    );
}
